import * as THREE from 'three';
import { MAP, PARKED_CARS, CAR } from '../utils/constants';
import { mulberry32 } from '../utils/rng';
import { getBuildingAABBs, type AABBXZ } from './collision';

export type ParkedCarsHandle = {
  group: THREE.Group;
  getAABBs: () => AABBXZ[];
};

function createParkedCarGeometry(): THREE.BufferGeometry {
  const body = new THREE.BoxGeometry(CAR.WIDTH * 0.9, CAR.HEIGHT * 0.8, CAR.LENGTH * 0.85);
  const cabin = new THREE.BoxGeometry(CAR.WIDTH * 0.75, CAR.HEIGHT * 0.5, CAR.LENGTH * 0.4);
  cabin.translate(0, CAR.HEIGHT * 0.4, -CAR.LENGTH * 0.05);

  const merged = new THREE.BufferGeometry();

  const bodyPos = body.attributes.position.array;
  const cabinPos = cabin.attributes.position.array;
  const positions = new Float32Array(bodyPos.length + cabinPos.length);
  positions.set(bodyPos, 0);
  positions.set(cabinPos, bodyPos.length);

  merged.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  merged.computeVertexNormals();

  return merged;
}

export function createParkedCars(seed: number = 42): ParkedCarsHandle {
  const group = new THREE.Group();
  group.name = 'ParkedCars';

  const rng = mulberry32(seed);
  const parkedAABBs: AABBXZ[] = [];

  const geometry = createParkedCarGeometry();

  const { HALF, GRID, ROAD_WIDTH, SIDEWALK_WIDTH } = MAP;
  const roadWithSidewalk = ROAD_WIDTH + SIDEWALK_WIDTH * 2;
  const totalRoadSpace = (GRID + 1) * roadWithSidewalk;
  const inner = MAP.SIZE - totalRoadSpace;
  const blockW = inner / GRID;

  const parkingSpots: { x: number; z: number; heading: number }[] = [];

  for (let i = 0; i <= GRID; i++) {
    const roadZ = -HALF + i * (roadWithSidewalk + blockW) + roadWithSidewalk / 2;

    for (let x = -HALF + 40; x < HALF - 40; x += 8 + rng() * 6) {
      if (rng() < 0.5) {
        const side = rng() < 0.5 ? 1 : -1;
        const z = roadZ + side * (ROAD_WIDTH * 0.35 + SIDEWALK_WIDTH);
        parkingSpots.push({ x, z, heading: side > 0 ? 0 : Math.PI });
      }
    }
  }

  for (let j = 0; j <= GRID; j++) {
    const roadX = -HALF + j * (roadWithSidewalk + blockW) + roadWithSidewalk / 2;

    for (let z = -HALF + 40; z < HALF - 40; z += 8 + rng() * 6) {
      if (rng() < 0.5) {
        const side = rng() < 0.5 ? 1 : -1;
        const x = roadX + side * (ROAD_WIDTH * 0.35 + SIDEWALK_WIDTH);
        parkingSpots.push({ x, z, heading: side > 0 ? Math.PI / 2 : -Math.PI / 2 });
      }
    }
  }

  for (let i = parkingSpots.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [parkingSpots[i], parkingSpots[j]] = [parkingSpots[j], parkingSpots[i]];
  }

  const buildingAABBs = getBuildingAABBs();

  const isValidSpot = (x: number, z: number): boolean => {
    const r = CAR.LENGTH * 0.6;
    for (const box of buildingAABBs) {
      if (
        x + r > box.minX &&
        x - r < box.maxX &&
        z + r > box.minZ &&
        z - r < box.maxZ
      ) {
        return false;
      }
    }

    for (const existing of parkedAABBs) {
      if (
        x + r > existing.minX &&
        x - r < existing.maxX &&
        z + r > existing.minZ &&
        z - r < existing.maxZ
      ) {
        return false;
      }
    }

    return true;
  };

  let placed = 0;
  for (const spot of parkingSpots) {
    if (placed >= PARKED_CARS.COUNT) break;
    if (!isValidSpot(spot.x, spot.z)) continue;

    const colorHex = PARKED_CARS.COLORS[Math.floor(rng() * PARKED_CARS.COLORS.length)];
    const material = new THREE.MeshStandardMaterial({
      color: colorHex,
      roughness: 0.4,
      metalness: 0.6,
      envMapIntensity: 0.8,
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.set(spot.x, CAR.HEIGHT * 0.4, spot.z);
    mesh.rotation.y = spot.heading;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    group.add(mesh);

    const halfW = CAR.WIDTH * 0.5;
    const halfL = CAR.LENGTH * 0.5;
    const cos = Math.cos(spot.heading);
    const sin = Math.sin(spot.heading);

    const corners = [
      { x: -halfW, z: -halfL },
      { x: halfW, z: -halfL },
      { x: -halfW, z: halfL },
      { x: halfW, z: halfL },
    ];

    let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
    for (const c of corners) {
      const wx = spot.x + c.x * cos - c.z * sin;
      const wz = spot.z + c.x * sin + c.z * cos;
      minX = Math.min(minX, wx);
      maxX = Math.max(maxX, wx);
      minZ = Math.min(minZ, wz);
      maxZ = Math.max(maxZ, wz);
    }

    parkedAABBs.push({ minX, maxX, minZ, maxZ });
    placed++;
  }

  const getAABBs = () => parkedAABBs;

  return { group, getAABBs };
}
